<template>
  <v-form ref="ADRESSE" @submit.prevent="sectionSave" lazy-validation class="section">
    <div>
      <section-header v-if="!hideTitle"
                      :ressource="ressource"
                      :isExtended="extended"
                      :dialog="dialog"
                      :picto="picto()"
                      :fiche=fiche
                      :isAbstract=isAbstract
                      @extended=setExtended
                      @closeDialog=closeDialog
                      ref="ADRESSE">
      </section-header>
      <div v-if="extended" id="field-container" :class="dialog ? 'p-0 p-md-3' : 'py-2'">
        <adresse-adresse1 v-if="isDisplayed('ADRESSE1')"
                          ref="ADRESSE1"
                          :theme="theme"
                          :showMissing="showMissing"
                          v-model:value=adresse1
                          :max="36"
                          @status=statusAdresse1
                          :over-ride-placeholder=placeholderAdresse1
                          :required="isRequired('ADRESSE1')"
                          :contextId=adresse.id
                          :readOnly="readOnly('ADRESSE1')"
                          :objType=objType
                          :fiche=fiche
                          :sectionName="sectionName">
        </adresse-adresse1>
        <adresse-adresse2 v-if="isDisplayed('ADRESSE2')"
                          ref="ADRESSE2"
                          :theme="theme"
                          :showMissing="showMissing"
                          v-model:value=adresse2
                          :max="36"
                          @status=statusAdresse2
                          :over-ride-placeholder=placeholderAdresse2
                          :required="isRequired('ADRESSE2')"
                          :contextId=adresse.id
                          :readOnly="readOnly('ADRESSE2')"
                          :objType=objType
                          :fiche=fiche
                          :sectionName="sectionName">
        </adresse-adresse2>
        <adresse-code-postal v-if="isDisplayed('CODE_POSTAL')"
                             ref="CODE_POSTAL"
                             :theme="theme"
                             :showMissing="showMissing"
                             v-model:value=codePostal
                             :min="5"
                             :max="5"
                             @status=statusCodePostal
                             :over-ride-placeholder=placeholderCodePostal
                             :required="isRequired('CODE_POSTAL')"
                             :contextId=adresse.id
                             :readOnly="readOnly('CODE_POSTAL')"
                             :objType=objType
                             :fiche=fiche
                             :sectionName="sectionName">
        </adresse-code-postal>
        <adresse-ville v-if="isDisplayed('VILLE')"
                       ref="VILLE"
                       :theme="theme"
                       :showMissing="showMissing"
                       v-model:value=ville
                       :max="32"
                       @status=statusVille
                       :over-ride-placeholder=placeholderVille
                       :required="isRequired('VILLE')"
                       :contextId=adresse.id
                       :readOnly="readOnly('VILLE')"
                       :objType=objType
                       :fiche=fiche
                       :sectionName="sectionName">
        </adresse-ville>
        <pays-select v-if="isDisplayed('PAYS_ID')"
                     ref="PAYS_ID"
                     :theme="theme"
                     :showMissing="showMissing"
                     v-model:value=paysId
                     :moduleId=moduleId
                     libelle="'Pays'"
                     newLabel=''
                     selectText=""
                     @status=statusPaysId
                     :requiredAttributs="requiredAttributs"
                     :autoSelect=true
                     :ressource=ressourcePaysSelect
                     :filters=paysIdFilter
                     :toggleReset=resetPaysId
                     :required="isRequired('PAYS_ID')"
                     :contextId=adresse.id
                     :readOnly="readOnly('PAYS_ID')"
                     :objType=objType
                     :fiche=fiche
                     :sectionName="sectionName">
        </pays-select>
      </div>
    </div>
    <div v-if="extended" class="d-flex flex-row-reverse">
      <loader-button v-if="saveButton"
                     class="ms-2"
                     type="submit"
                     :loading="saveLoading"
                     :label="saveLabel"
                     :disabled="!updatedAndValid">
      </loader-button>
      <div v-for="(button, index) in extraButtons" :key="index">
        <loader-button class="ms-2"
                       :outlined="button.outlined"
                       @callback="button.callback()"
                       :type="button.type"
                       :label="button.label"
                       :loading="button.loading"
                       :disabled="button.disabled">
        </loader-button>
      </div>
    </div>
    <div class="d-flex flex-row-reverse" v-if="saveFailed && saveButton && extended">
      <p class="text-12 text-bold error-text">{{errorTxt}}</p>
    </div>
  </v-form>
</template>

<script lang="ts">
  import { defineComponent } from "vue";
  import CrudAdresse from '@/generated/cruds/CrudAdresse';
  // eslint-disable-next-line
  import { GenControl, GenSection, RessourceGenControl, RessourceGenSection } from '@/generated/ressources/generatedRessources';
  import { FwkButtons, RessourceFwkButton, RessourceFwkSection, FwkSections } from '@/ressources/globalRessources';
  import { Sections } from '@/generated/classes/AppSchema';
  import Button, { ButtonCallback } from '@/framework/base/classes/Button';
  import { ColumnFilter, ListChoix } from '@/extend/classes/ListData';
  import { ObjTypes } from '@/generated/classes/ObjTypes';

  export default defineComponent({
    extends:  CrudAdresse,
    components: {},
    props: {
      hideTitle: {
        type: Boolean,
      },
      isAbstract: {
        type: Boolean,
      },
    },

    data() {
      return {
        sectionName: Sections.ADRESSE,
        displayMap: new Map<string, Boolean>,
        readOnlyMap: new Map<string, Boolean>,
        requiredMap: new Map<string, Boolean>,
        placeholderAdresse1: null,
        placeholderAdresse2: null,
        placeholderCodePostal: null,
        placeholderVille: null,
      }
    },

    computed: {
      saveLabel () { 
        return RessourceFwkButton(FwkButtons.ENREGISTRER).label;
      },

      errorTxt () { 
        return RessourceFwkSection(FwkSections.ERROR_TEXT).label;
      },

      ressourcePaysSelect () { return RessourceGenControl(GenControl.ADRESSE_PAYS_SELECT); },

      ressource () { return RessourceGenSection(GenSection.ADRESSE); },

      fields (): string[] {
        return ['ADRESSE1', 'ADRESSE2', 'CODE_POSTAL', 'VILLE', 'PAYS_ID'];
      },

      extraButtons (): Button[] {
        return [];
      },

      paysIdFilter (): ColumnFilter[] {
        const res: ColumnFilter[] = [];
        return res;
      },
    },
    methods: {
      getRequiredAttributs(): string[] {
        const res: string[] = [];
        if (this.requiredAttributs) {
          this.requiredAttributs.forEach((attrib) => {
            const value = attrib.split(".");
            if (value[0] == this.sectionName) {
              res.push(value[1])
            }
          })
        }
        return res;
      },

      requiredMissing(requiredAttributs: string[], value: string): boolean {
        return requiredAttributs.length > 0 && requiredAttributs.includes(value);;
      },

      readOnly (value: string): boolean {
        let res = false;
        if (!this.editAdresse.canUpdate) {
          return true;
        }
        if (this.readOnlyMap.size == 0) {
          this.readOnlyMap.set("ADRESSE1", false);
          this.readOnlyMap.set("ADRESSE2", false);
          this.readOnlyMap.set("CODE_POSTAL", false);
          this.readOnlyMap.set("VILLE", false);
          this.readOnlyMap.set("PAYS_ID", false);
        }
        const readOnly = this.readOnlyMap.get(value);
        if (readOnly != undefined) {
          res = readOnly;
        }
        return res;
      },

      isDisplayed (value: string): boolean {
        let res = true;
        if (this.displayMap.size == 0) {
        const requiredAttributs = this.getRequiredAttributs();
          this.displayMap.set("ADRESSE1", true);
          this.displayMap.set("ADRESSE2", this.requiredMissing(requiredAttributs, "ADRESSE2") || !!this.fiche);
          this.displayMap.set("CODE_POSTAL", true);
          this.displayMap.set("VILLE", true);
          this.displayMap.set("PAYS_ID", true);
        }
        const display = this.displayMap.get(value);
        if (display != undefined) {
          res = display;
        }
        return res;
      },

      isRequired (value: string): boolean {
        let res = true;
        const requiredAttributs = this.getRequiredAttributs();
        if (this.requiredMap.size == 0) {
          this.requiredMap.set("ADRESSE1", true);
          this.requiredMap.set("ADRESSE2", this.requiredMissing(requiredAttributs, "ADRESSE2") || !this.fiche);
          this.requiredMap.set("CODE_POSTAL", true);
          this.requiredMap.set("VILLE", true);
          this.requiredMap.set("PAYS_ID", true);
        }
        const required = this.requiredMap.get(value);
        if (required != undefined) {
          res = required;
        }
        return res;
      },

      picto (): string {
        return 'SectionPictoAdresse' 
      },

      sectionSave (): void {
        return this.save();
      }
    }
  })
</script>
